<script setup lang="ts">
import { Keyboard, Mousewheel, Navigation, A11y } from 'swiper/modules';
import a11yRawCSS from 'swiper/element/css/a11y?raw';
import navigationRawCSS from 'swiper/element/css/navigation?raw';
import keyboardRawCSS from 'swiper/element/css/keyboard?raw';
import mousewheelRawCSS from 'swiper/element/css/mousewheel?raw';
import { register } from 'swiper/element';

register();

const props = defineProps<{
  images: Array<Image>;
  aspectRatio?: string;
  slidesPerView?: number;
  caption?: string;
}>();

const modules = [Keyboard, Mousewheel, Navigation, A11y];

const moreThanOneSlidePerView = (typeof props.slidesPerView !== "undefined" && props.slidesPerView > 1) || typeof props.slidesPerView === "undefined";

const swiperRef = ref(null);

const swiperParams = {
    modules,
    slidesPerView: props.slidesPerView || 1,
    spaceBetween: 0,
    breakpoints: {
      640: {
        slidesPerView: props.aspectRatio || 2,
        spaceBetween: moreThanOneSlidePerView ? 16 : 0,
      },
      768: {
        slidesPerView: props.slidesPerView || 3,
        spaceBetween: moreThanOneSlidePerView ? 40 : 0,
      },
    },
    injectStyles: [
      `
      ${a11yRawCSS}

      ${navigationRawCSS}

      ${keyboardRawCSS}

      ${mousewheelRawCSS}
      `,
    ],
};
  
onMounted(() => {
  if (swiperRef.value) {
    Object.assign(swiperRef.value, swiperParams);
    swiperRef.value.initialize();
  }
});
</script>

<template>
  <figure class="w-full">
    <swiper-container
      init="false"
      ref="swiperRef"
      class="h-full w-full"
      a11y="true"
      navigation="true"
      keyboard="true"
      grabCursor="true"
    >
      <swiper-slide v-for="image of images" :key="image.id" :lazy="true">
        <BaseImage v-bind="image" :aspect-ratio="aspectRatio" />
      </swiper-slide>
    </swiper-container>
    <figcaption v-if="caption" class="mt-4 text-sm">{{ caption }}</figcaption>
  </figure>
</template>

<style lang="scss">
swiper-container::part(button-prev),
swiper-container::part(button-next) {
  @apply bg-brandLight text-brandDark border-brandLight;

  width: calc(var(--swiper-navigation-size) / 2);
  height: calc(var(--swiper-navigation-size) / 2);
  margin-top: 0;
  transform: translateY(-50%);
  border-radius: 100%;
  border-width: calc((var(--swiper-navigation-size) / 2) / 2);
  border-style: solid;
}

swiper-slide::part(preloader) {
  --swiper-preloader-color: #F3B06D;
}
</style>